<script lang="ts">
import { getAuthToken } from "./services/auth.service";
import { type NotificationModalInfo } from "@/types/global.types";
import { useGlobalStore } from "@/stores/globalStore";
import { useFileStore } from "@/stores/fileStore";
import { serverUrls } from "./utils/constants";
import ChatComponent, { eventBusChat } from "@headshots/chat-app";
import { useRoute } from "vue-router";
import { ref } from "vue";

export default {
  setup() {
    const globalStore = useGlobalStore();
    const fileStore = useFileStore();
    const route = useRoute();
    const authToken = getAuthToken()?.toString();

    const isAppReady = ref(false);

    setTimeout(() => {
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      globalStore.isMobile = isMobile;
      globalStore.chatMenuOpen = isMobile;
      isAppReady.value = true;
    }, 800);

    return {
      globalStore,
      fileStore,
      route,
      authToken,
      isAppReady,
      eventBusChat,
    };
  },
  components: {
    ChatComponent,
  },

  created() {
    // Set custom title and favicon for PRA environment
    if (import.meta.env.VITE_APP_PRA === 'ACTIVE') {
      // Set the document title
      document.title = 'PRA - Renomino';
      // Set the favicon
      const link =
        document.querySelector("link[rel~='icon']") ||
        document.createElement("link");

      link.rel = "icon";
      link.href = "/favicon-pra.ico";
      document.head.appendChild(link);

      // Set a CSS variable to change the primary color for PRA environment
      document.documentElement.style.setProperty("--factstory", "#0bcd52");
      document.documentElement.style.setProperty("--factstorylight", "#84ef90");
      document.documentElement.style.setProperty("--bg", "#0F2219");
      document.documentElement.style.setProperty("--bg2", "#21312A");
      document.documentElement.style.setProperty("--bg3", "#2E3B33");
    }
    eventBusChat.on("navigate-to-athlete", this.navigateToAthlete);
    eventBusChat.on("unseen-notif", this.handleUnseen);
    eventBusChat.on("mention-notif", this.showMentionNotification);
    this.$eventBus.on("displayinfo", this.displayInfo);
    this.$eventBus.on("showTooltip", this.displayTooltip);
    this.$eventBus.on("hideTooltip", this.hideTooltip);
  },
  unmounted() {
    eventBusChat.off("navigate-to-athlete", this.navigateToAthlete);
    eventBusChat.off("unseen-notif", this.handleUnseen);
    eventBusChat.off("mention-notif", this.showMentionNotification);
    this.$eventBus.off("displayinfo", this.displayInfo);
    this.$eventBus.off("showTooltip", this.displayTooltip);
    this.$eventBus.off("hideTooltip", this.hideTooltip);
  },
  computed: {
    tokenChat() {
      return localStorage.getItem("authToken") || "";
    },
    locale() {
      return this.$i18n.locale || localStorage.getItem("locale") || "en";
    },
    backUrl() {
      return serverUrls.authUrl || null;
    },
  },
  watch: {
    $route(to, from) {
      this.hideTooltip();
    },
  },
  methods: {
    navigateToAthlete(folder: { athleteId: string }) {
      this.$router.push(`/athlete-folder/${folder.athleteId}`);
    },
    showMentionNotification(mention: {
      chatId: string;
      sender: string;
      message: string;
    }) {
      this.globalStore.mentionChatData = {
        chatId: mention.chatId,
        sender: mention.sender,
        message: mention.message,
      };
    },
    closeMentionNotification() {
      this.globalStore.mentionChatData = {
        chatId: "",
        sender: "",
        message: "",
      };
    },
    handleUnseen(total: number) {
      this.globalStore.chatUnreadMsg = total;
    },
    eventClosed() {
      this.globalStore.chatMenuOpen = false;
    },
    displayInfo(infodata: NotificationModalInfo) {
      this.globalStore.ModalInfoData = {
        show: true,
        title: infodata.title ? infodata.title : "",
        text: infodata.text ? infodata.text : "",
        route: infodata.route ? infodata.route : "",
        textlink: infodata.textlink ? infodata.textlink : "",
        error: infodata.error ? infodata.error : false,
        valid: infodata.valid ? infodata.valid : false,
        rawMessage: infodata.rawMessage ? infodata.rawMessage : "",
      };
    },
    displayTooltip(event: { content: string; event: MouseEvent }) {
      this.globalStore.tooltip = true;
      this.globalStore.tooltipText = event.content;
      this.globalStore.tooltipPosition = event.event;
    },
    hideTooltip() {
      this.globalStore.tooltip = false;
      this.globalStore.tooltipText = "";
      this.globalStore.tooltipPosition = null;
    },
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<template>
  <!-- Modal Guide for each roles to learn how to use the app -->
  <transition name="fade" mode="out-in">
    <ModalGuideItem
      v-if="globalStore.showModalGuide"
      @close="() => (globalStore.showModalGuide = false)"
    >
      <GuideContentItem :guideKey="globalStore.guideKey" />
    </ModalGuideItem>
  </transition>

  <!-- Basic Loader for back-end response -->
  <LoaderComponent v-if="globalStore.showLoader || !isAppReady" />

  <!-- Loader for file sending request -->
  <LoaderProgressComponent v-if="globalStore.showProgressLoader" />

  <!-- Icon for Chat and unread messages -->
  <div
    class="unread-message"
    v-if="
      globalStore.chatUnreadMsg > 0 &&
      isAppReady &&
      !globalStore.isDashboardRole
    "
  >
    {{ globalStore.chatUnreadMsg > 9 ? "9+" : globalStore.chatUnreadMsg }}
  </div>
  <ChatIcon
    id="chat-icon"
    v-if="
      globalStore.hasLoggedInBefore &&
      globalStore.hasToken &&
      isAppReady &&
      !globalStore.isDashboardRole
    "
    @click="globalStore.chatMenuOpen = true"
  />

  <!-- Select menu handling i18n plugin locale -->
  <LanguageSelector id="language-menu" />

  <!-- Modal for Notification of errors -->
  <transition name="fade-right" mode="out-in">
    <NotificationModalComponent
      :show="globalStore.ModalInfoData.show"
      :title="globalStore.ModalInfoData.title"
      :text="globalStore.ModalInfoData.text"
      :route="globalStore.ModalInfoData.route"
      :textlink="globalStore.ModalInfoData.textlink"
      :error="globalStore.ModalInfoData.error"
      :valid="globalStore.ModalInfoData.valid"
      :rawMessage="globalStore.ModalInfoData.rawMessage"
    ></NotificationModalComponent>
  </transition>

  <!-- Tooltip all over the app -->
  <transition name="fade" mode="out-in">
    <TooltipItem
      :content="globalStore.tooltipText"
      v-if="globalStore.tooltip"
      :position="globalStore.tooltipPosition"
    />
  </transition>

  <!-- Modal to update the userName in the first connection all over the app -->
  <transition name="fade" mode="out-in">
    <FirstConnectionModal v-if="!globalStore.hasLoggedInBefore" />
  </transition>

  <!-- Side menu for administration pages -->
  <AdminMenuComponent
    v-if="
      (globalStore.isAdmin && isAppReady) ||
      (globalStore.isDashboardRole && isAppReady)
    "
  />

  <!-- Views -->
  <router-view
    v-if="isAppReady"
    id="main-content"
    class="main-content-all"
    :class="[globalStore.isAdmin ? 'admin-width' : 'full-width']"
    :style="{
      backgroundColor: route.meta.darkBackground ? 'var(--bg)' : 'var(--bg2)',
      display:
        globalStore.isMobile && globalStore.chatMenuOpen && globalStore.hasToken
          ? 'none'
          : '',
    }"
    v-slot="{ Component }"
  >
    <transition name="fade" mode="out-in">
      <component :is="Component" />
    </transition>
  </router-view>

  <!-- Chat application component -->
  <div
    id="chat-content"
    v-show="
      globalStore.chatMenuOpen &&
      tokenChat &&
      globalStore.hasToken &&
      backUrl &&
      !globalStore.isDashboardRole
    "
  >
    <div
      @click="globalStore.chatMenuOpen = false"
      id="close-chat"
      v-if="!globalStore.isMobile"
    >
      <CloseIcon />
    </div>
    <ChatComponent
      v-if="
        isAppReady &&
        tokenChat &&
        globalStore.hasToken &&
        backUrl &&
        !globalStore.isDashboardRole
      "
      id="chat"
      :apiUrl="backUrl"
      :token="tokenChat"
      :lang="locale"
    />
  </div>

  <!-- Notification for new set of duplicated athlete -->
  <transition name="fade" mode="out-in">
    <NotificationChatComponent
      v-if="!globalStore.isMobile && globalStore.mentionChatData.chatId"
      :chatOpen="globalStore.chatMenuOpen"
      :chatId="globalStore.mentionChatData.chatId"
      :sender="globalStore.mentionChatData.sender"
      :message="globalStore.mentionChatData.message"
      @goToChat="
        (chatId: string) => {
          globalStore.chatMenuOpen = true;
          eventBusChat.emit('open-chat-mention', chatId);
          closeMentionNotification();
        }
      "
    ></NotificationChatComponent>
  </transition>
</template>
